import './access360Widget.less'
import { RootState } from '../../../../rootReducer'
import { getShowLoader } from '../../../../reducers/requestReducer'
import {
  ACTION_FETCH_ACCESS360_WIDGET,
  fetchAccess360Widget
} from '../../../accessControl/accessControlSlice'
import {
  Access360Widget,
  AccessControlAccessTypes,
  IGetAccessControlUsersFilterParams
} from '../../../accessControl/types'
import LoaderView from '../../../../components/Loader/loader'
import ChartBarSimple from '../../../../components/ChartBarSimple/chartBarSimple'
import { ChartColors, getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../constants'
import {
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_TYPES,
  FILTER_ACCESS,
  URL_ACCESS_CONTROL,
  URL_ACCESS_CONTROL_DASHBOARD,
  URL_DATA_SOURCES,
  URL_OBJECTS_ACCESS,
  URL_PII_OBJECTS
} from '../../../../constants'
import useUrlContext from '../../../../hooks/useUrlContext'
import { getUpdatedUrl } from '../../../../utils/urlUtil'
import CustomTooltip from '../../../../components/ChartTooltipV2'
import { approximatedValue, getWindowProps } from '../../../../utils'
import useWindowSize from '../../../../hooks/useWindowResize'
import { mockDsAccess360WidgetData } from '../../renderer/utils/widgetMockdata'
import { connect } from 'react-redux'
import { FormattedNumber, useIntl } from 'react-intl'
import React, { useEffect } from 'react'
import { Typography } from '@lightbeamai/design-system'
import { useHistory, Link } from 'react-router-dom'
import { ArrowSquareOut as IconArrowSquareOut } from 'phosphor-react'

interface OwnProps {
  useMockData?: boolean
  chartBarAlignment?: 'vertical' | 'horizontal'
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
}

interface StateProps {
  widget?: Access360Widget
  showLoader: boolean
}

interface DispatchProps {
  fetchAccess360Widget: (
    params: IGetAccessControlUsersFilterParams & { [UNIQUE_WIDGET_INDEX]?: string }
  ) => void
}

type Access360WidgetComponentProps = StateProps & DispatchProps & OwnProps

const Access360Widget = (props: Access360WidgetComponentProps) => {
  const {
    widget,
    showLoader,
    chartBarAlignment = 'horizontal',
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    widgetDatasourceType,
    fetchAccess360Widget
  } = props
  const intl = useIntl()
  const history = useHistory()

  const context = useUrlContext()
  const datasourceId = widgetDatasourceId || context.contextFilters[DATA_SOURCE_ID] || ''

  const windowProps = getWindowProps(useWindowSize())
  const { isLargeScreen } = windowProps

  useEffect(() => {
    if (useMockData) return
    fetchAccess360Widget({
      datasourceId,
      [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
    })
  }, [useMockData, fetchAccess360Widget])

  const openAccessText = intl.formatMessage({ id: 'accessControl.openAccess' })
  const excessiveAccessText = intl.formatMessage({ id: 'accessControl.excessiveAccess' })
  const externalAccessText = intl.formatMessage({ id: 'accessControl.externalAccess' })

  const chartsData = [
    {
      name: openAccessText,
      value: widget?.openAccessObjects || 0,
      color: ChartColors.Purple,
      dataKey: AccessControlAccessTypes.open
    },
    ...(widgetDatasourceType === DATA_SOURCE_TYPES.aws
      ? [
          {
            name: externalAccessText,
            value: widget?.externalAccessObjects || 0,
            color: ChartColors.Blue,
            dataKey: AccessControlAccessTypes.external
          }
        ]
      : [
          {
            name: excessiveAccessText,
            value: widget?.execessiveAccessObjects || 0,
            color: ChartColors.Blue,
            dataKey: AccessControlAccessTypes.excessive
          }
        ])
  ]

  const countsFormatter = (value) => intl.formatNumber(value, { notation: 'compact' })

  const dashboardLink = datasourceId
    ? getUpdatedUrl({
        pathName: URL_DATA_SOURCES + `/${datasourceId}` + URL_ACCESS_CONTROL_DASHBOARD,
        queryParams: {
          ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType }),
          ...(datasourceId && { [DATA_SOURCE_ID]: datasourceId })
        }
      })
    : URL_ACCESS_CONTROL

  const getBarItemLink = (val) => {
    return datasourceId
      ? getUpdatedUrl({
          pathName: URL_DATA_SOURCES + `/${datasourceId}` + URL_OBJECTS_ACCESS,
          pageName: URL_OBJECTS_ACCESS,
          pageQueryParams: {
            [FILTER_ACCESS]: val
          },
          queryParams: {
            ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType }),
            ...(datasourceId && { [DATA_SOURCE_ID]: datasourceId })
          }
        })
      : getUpdatedUrl({
          pathName: URL_ACCESS_CONTROL + URL_PII_OBJECTS,
          pageName: URL_PII_OBJECTS,
          pageQueryParams: {
            [FILTER_ACCESS]: val
          }
        })
  }

  const handleClick = (val) => {
    const url = getBarItemLink(val?.dataKey || val?.payload?.dataKey)
    history.push(url)
  }

  return (
    <div
      className="lb-widget-card access-360-widget xs-p-16"
      data-test-id="dashboard-access-360-widget"
    >
      <div className="access-360-widget-header">
        <Typography color="primary" variant="h6">
          {intl.formatMessage({ id: 'accessControl.access360' })}
        </Typography>
        <Link to={dashboardLink} className="dashboard-link" target={!datasourceId ? '_blank' : ''}>
          <Typography color="primary" type="medium" variant="h6">
            {intl.formatMessage({ id: 'accessControl.access360.dashboard' })}
          </Typography>
          <IconArrowSquareOut size="16" color="var(--color-primary-light)" weight="bold" />
        </Link>
      </div>

      <div className={`access-control-chart-wrapper ${chartBarAlignment}`}>
        <div className="dashboard-labels-count-wrapper">
          <div className="label-items">
            {widgetDatasourceType !== DATA_SOURCE_TYPES.aws && (
              <div className="label-item">
                <Typography color="primary" type="strong" variant="h4">
                  <FormattedNumber value={widget?.usersCount || 0} notation="compact">
                    {approximatedValue}
                  </FormattedNumber>
                </Typography>
                <Typography color="secondary" type="medium" variant="baseMinus2">
                  {intl.formatMessage({ id: 'accessControl.access360.usersAccess' })}
                </Typography>
              </div>
            )}
            <div className="label-item">
              <Typography color="primary" type="strong" variant="h4">
                <FormattedNumber value={widget?.objectsCount || 0} notation="compact" />
              </Typography>
              <Typography color="secondary" type="medium" variant="baseMinus2">
                {intl.formatMessage({ id: 'accessControl.access360.sensitiveObjects' })}
              </Typography>
            </div>
          </div>
        </div>
        <div className="charts-wrapper access-control-bar-charts">
          <ChartBarSimple
            className="xs-mt-24 v2-chart"
            data={chartsData}
            cartesianGridHorrizontal
            tickFormatter={{
              fontSize: '0.72rem',
              fill: ChartColors.SecondaryText,
              width: 60,
              textAnchor: 'middle'
            }}
            multiColored
            chartMargin={{ left: 20, right: 20, top: 0, bottom: 0 }}
            barSize={60}
            chartResponsiveHeight={isLargeScreen ? 240 : 200}
            {...(isLargeScreen ? { xAxisHeight: 55 } : {})}
            yLabel={intl.formatMessage({ id: 'accessControl.objects' })}
            xAxisAngle={0}
            YTickFormatter={countsFormatter}
            yAxisLabelPadding={{ dx: -12, dy: -6 }}
            customToolTip={
              <CustomTooltip title={intl.formatMessage({ id: 'accessControl.objects' })} />
            }
            tickMargin={4}
            xAxisTickInterval={0}
            onBarItemClick={handleClick}
          />
        </div>
      </div>
      <LoaderView size="small" showLoader={showLoader} />
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  widget: ownProps?.useMockData
    ? mockDsAccess360WidgetData.widget
    : state.accessControl.dashboardWidgets.access360?.[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ],

  showLoader: getShowLoader(ACTION_FETCH_ACCESS360_WIDGET)
})

const mapDispatchToProps: DispatchProps = { fetchAccess360Widget }

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(Access360Widget)
